import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Slide,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { AuthContext, UserContext } from '@/store';
import { infoList } from '@/lib/utils/DataFormat';
import FollowToggle from '@/js/shared/components/lists/FollowToggle';
import EntityListItem from './EntityListItem';
import CoChipSmall from './CoChipSmall';
import UserAvatar from '@/components/ui/userAvatar';

const useStyles = (theme) => ({
  avatarBg: {
    paddingTop: '8px!important',
    paddingBottom: '8px!important',
    backgroundColor: `${theme.palette.background.shadedown_20} !important`,
    overflow: 'hidden !important',
    flexShrink: `0 !important`,
    position: 'relative !important',
  },
  isOnline: {
    display: 'inline-block !important',
    backgroundColor: '#000000aa !important',
    position: 'absolute !important',
    left: '8px !important',
    top: '8px !important',
    padding: '2px 12px 3px 23px !important',
    color: '#fff !important',
    borderRadius: '15px !important',
    lineHeight: '19px !important',
    '&::before': {
      position: 'absolute !important',
      top: '7px !important',
      left: '7px !important',
      content: '""',
      display: 'block !important',
      width: '10px !important',
      height: '10px !important',
      borderRadius: '10px !important',
      backgroundColor: '#29ff00 !important',
    },
  },
  paperHolder: {
    alignItems: 'flex-start !important',
    justifyContent: 'flex-end !important',
  },
  paperBg: {
    backgroundColor: `${theme.palette.background.default} !important`,
    margin: '40px 8px 32px 8px !important',
    width: '380px !important',
    maxWidth: 'calc(100% - 16px) !important',
  },
  dialogContent: {
    padding: '4px 16px 16px 16px !important',
    width: '380px !important',
    maxWidth: '100% !important',
    boxSizing: 'border-box !important',
  },
  dialogTitle: {
    padding: '8px 16px 0px 16px !important',
  },
  dialogBg: {
    backgroundColor: 'rgba(255, 255, 255, 0.11) !important',
  },
  infoCompany: {
    color: '#777777 !important',
  },
  infoTitle: {
    textTransform: 'uppercase !important',
    opacity: '0.7 !important',
  },
  infoCity: {
    fontWeight: '600 !important',
  },
  info_description: {
    whiteSpace: 'pre-wrap !important',
    textAlign: 'left !important',
    padding: '15px !important',
  },
  buttonsPrevNext: {
    display: 'flex !important',
    justifyContent: 'space-between !important',
    backgroundColor: `${theme.palette.background.shadedown_20} !important`,
  },
  tagFont: {
    '& > *': {
      fontSize: '13px !important',
    },
  },
});

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const ParticipantPopup = (props, ref) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { userState, actions } = useContext(UserContext);
  const { authState } = useContext(AuthContext);
  const [dialogState, setDialog] = useState({
    open: false,
    user: null,
    userlist: null,
  });

  const isMe =
    authState.user &&
    dialogState.user &&
    authState.user.id === dialogState.user.id;

  const handleDialogClose = () => {
    setDialog({ open: false, user: null, userlist: null });
  };

  const updateUser = (userData) => {
    setDialog((prevState) => ({ ...prevState, user: userData }));
  };

  const stepInList = (step) => {
    const index = dialogState.userlist.findIndex(
      (item) => item.id === dialogState.user.id
    );
    const nextUserIndex =
      (dialogState.userlist.length + index + step) %
      dialogState.userlist.length;
    setDialog({
      ...dialogState,
      user: dialogState.userlist[nextUserIndex],
    });
    actions.loadPublicProfile(dialogState.userlist[nextUserIndex].id);
  };

  useEffect(() => {
    if (dialogState.user && dialogState.user.id && userState.userLoaded) {
      setDialog({ ...dialogState, user: userState.user });
    }
  }, [dialogState.user && dialogState.user.id, userState.userLoaded]);

  useImperativeHandle(ref, () => ({
    setState: (options) => {
      setDialog({ ...dialogState, ...options });
      if (options.user) {
        actions.loadPublicProfile(options.user.id);
      }
    },
  }));

  return (
    <Dialog
      style={{ zIndex: 1350 }}
      open={dialogState.open}
      onClose={handleDialogClose}
      TransitionComponent={Transition}
      scroll="paper"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        ...classes.dialogBg,
        '& .MuiDialog-container': {
          ...classes.paperHolder,
        },
        '& .MuiPaper-root': {
          ...classes.paperBg,
        },
      }}
    >
      {dialogState.user && (
        <>
          {dialogState.userlist && dialogState.userlist.length > 1 && (
            <Box sx={classes.buttonsPrevNext}>
              <Button
                startIcon={<Icon>chevron_left</Icon>}
                size="small"
                color="primary"
                onClick={() => stepInList(-1)}
              >
                {window.LANG.ECARD_PREV}
              </Button>
              <Button
                endIcon={<Icon>chevron_right</Icon>}
                size="small"
                color="primary"
                onClick={() => stepInList(1)}
              >
                {window.LANG.ECARD_NEXT}
              </Button>
            </Box>
          )}

          <DialogContent align="center" sx={classes.avatarBg}>
            <UserAvatar
              customSize={{ width: 150, height: 150, fontSize: 30 }}
              user={dialogState.user}
              rerender={true}
            />
            {dialogState.user.isOnline && (
              <Box sx={classes.isOnline}>online</Box>
            )}
          </DialogContent>
          <DialogTitle
            sx={classes.dialogTitle}
            id="alert-dialog-title"
            align="center"
          >
            {dialogState.user.full_name}
          </DialogTitle>

          <DialogContent sx={classes.dialogContent} align="center">
            {dialogState.user.fullData ? (
              <Box>
                {dialogState.user.title && (
                  <Box sx={classes.infoTitle}>{dialogState.user.title}</Box>
                )}
                {(dialogState.user.company ||
                  dialogState.user.location_city) && (
                  <Box pb={1} sx={classes.infoCity}>
                    {infoList([
                      dialogState.user.company,
                      dialogState.user.location_city,
                    ])}
                  </Box>
                )}
                {!isMe && (
                  <>
                    <FollowToggle
                      updatedCallback={updateUser}
                      user={dialogState.user}
                    />
                  </>
                )}
                <br />
                <Box component="span" sx={classes.tagFont}>
                  {dialogState.user.tags
                    .filter((tag) => tag.type === 'skill')
                    .map((tag) => (
                      <CoChipSmall
                        key={tag.id}
                        tagtype={tag.type}
                        label={tag.title}
                      />
                    ))}
                  {dialogState.user.tags
                    .filter((tag) => tag.type === 'scouting')
                    .map((tag) => (
                      <CoChipSmall
                        key={tag.id}
                        tagtype={tag.type}
                        label={tag.title}
                      />
                    ))}
                </Box>
                {dialogState.user.entities.map((ent) => (
                  <Box p={1} key={ent.id}>
                    <EntityListItem
                      url={`/live/item/${ent.id}`}
                      entity={ent}
                      // history={props.history}
                    />
                  </Box>
                ))}
                <br />
                {dialogState.user.tags && dialogState.user.tags.length > 0 && (
                  <br />
                )}
                <Box sx={classes.info_description}>
                  {dialogState.user.info_description}
                </Box>
              </Box>
            ) : (
              <Box align="center">
                <CircularProgress disableShrink />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="default">
              {window.LANG.ECARD_CLOSE}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default forwardRef(ParticipantPopup);
